import type { RefObject } from 'react';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, xcss } from '@atlaskit/primitives';
import WorldIcon from '@atlaskit/icon/core/globe';
import type { NewCoreIconProps } from '@atlaskit/icon';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { isFedRamp, isIsolatedCloud } from '@atlassian/atl-context';

import { CopyLinkButton, type PageMode } from '@confluence/copy-content-link';
import { fg } from '@confluence/feature-gating';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller';
import { WorldGreenDotGlyph } from '../../../svg/WorldGreenDotIcon-svg';

import { SlackIcon } from './icons/SlackIconColorized';

const FooterStyles = xcss({
	boxSizing: 'border-box',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
});

const i18n = defineMessages({
	copyLink: {
		id: 'share-and-restrict-dialog.footer.copy-link',
		defaultMessage: 'Copy link',
		description: 'Text for the share and restrict dialog footer button that copies a link',
	},
	publicLink: {
		id: 'share-and-restrict-dialog.footer.public-link',
		defaultMessage: 'Public link',
		description:
			'Text for the share and restrict dialog footer button that opens the Public Link dialog',
	},
	publicLinkIsOn: {
		id: 'share-and-restrict-dialog.footer.public-link-is-on',
		defaultMessage: 'Public link is on',
		description:
			'Text for the share and restrict dialog footer button that opens the Public Link dialog, and also signifies that public link is on/enabled for this content',
	},
	slack: {
		id: 'share-and-restrict-dialog.footer.slack',
		defaultMessage: 'Slack',
		description:
			'Text for the share and restrict dialog footer button that opens a slack sharing dialog',
	},
});

export type DistributionFooterProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	pageMode: PageMode;
	isLockedOut: boolean;
	isUnpublishedDraft: boolean;
	isLivePage: boolean;
	onSlackClick: () => void;
	onPublicLinkClick: () => void;
	isPublicLinkOn: boolean;
	isPublicLinkEditAllowedForContentType: boolean;
	lastTriggeredDialogVariant: 'publicLink' | 'shareToSlack' | 'accessRequests' | null;
	publicLinkButtonRef: RefObject<HTMLButtonElement>;
	shareToSlackButtonRef: RefObject<HTMLButtonElement>;
	clearElementToFocus: () => void;
};

export const DistributionFooter = ({
	contentId,
	contentType,
	spaceKey,
	pageMode,
	isLockedOut,
	isUnpublishedDraft,
	isLivePage,
	onSlackClick,
	onPublicLinkClick,
	isPublicLinkOn,
	lastTriggeredDialogVariant,
	publicLinkButtonRef,
	shareToSlackButtonRef,
	clearElementToFocus,
}: DistributionFooterProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFedRampEnv = isFedRamp();
	const isIsolatedCloudEnv = isIsolatedCloud() && fg('hide-share-to-slack-for-ic');
	const shouldShowSlackButton = !isFedRampEnv && !isIsolatedCloudEnv && !isUnpublishedDraft;
	const shouldShowPublicLinkButton =
		!isUnpublishedDraft && (contentType === 'page' || contentType === 'whiteboard');

	useEffect(() => {
		if (!lastTriggeredDialogVariant) {
			return;
		}

		let elementWasFocused = false;

		if (lastTriggeredDialogVariant === 'publicLink' && !!publicLinkButtonRef?.current) {
			publicLinkButtonRef.current.focus();
			elementWasFocused = true;
		} else if (lastTriggeredDialogVariant === 'shareToSlack' && !!shareToSlackButtonRef?.current) {
			shareToSlackButtonRef.current.focus();
			elementWasFocused = true;
		}

		if (elementWasFocused) {
			clearElementToFocus();
		}
	}, [lastTriggeredDialogVariant, publicLinkButtonRef, shareToSlackButtonRef, clearElementToFocus]);

	const iconProps: Partial<NewCoreIconProps> = {
		label: '',
		...(isPublicLinkOn ? { secondaryColor: token('color.icon.accent.green') } : {}),
	};
	const Icon = isPublicLinkOn ? WorldGreenDotGlyph : WorldIcon;

	const handleSlackClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ShareToSlackFooterButton',
				attributes: {
					contentId,
					contentType,
					isLivePage,
					pageMode,
				},
			},
		}).fire();
		onSlackClick();
	};
	/* eslint-disable @atlassian/a11y/anchor-is-valid*/
	return (
		<Inline grow="fill" spread="space-between" alignBlock="center" xcss={FooterStyles}>
			<Inline space="space.100">
				<CopyLinkButton
					buttonAppearance="subtle"
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					pageMode={pageMode}
					isDraft={isUnpublishedDraft}
					isLivePage={isLivePage}
					source={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
					buttonText={intl.formatMessage(i18n.copyLink)}
					isDisabled={isLockedOut}
				/>

				{shouldShowSlackButton && (
					<Button
						appearance="subtle"
						iconBefore={SlackIcon}
						onClick={handleSlackClick}
						isDisabled={isLockedOut}
						ref={shareToSlackButtonRef}
					>
						{intl.formatMessage(i18n.slack)}
					</Button>
				)}

				{shouldShowPublicLinkButton && (
					<Button
						appearance="subtle"
						iconBefore={(parentProps) => <Icon {...parentProps} {...iconProps} />}
						onClick={onPublicLinkClick}
						isDisabled={isLockedOut}
						ref={publicLinkButtonRef}
					>
						{isPublicLinkOn
							? intl.formatMessage(i18n.publicLinkIsOn)
							: intl.formatMessage(i18n.publicLink)}
					</Button>
				)}
			</Inline>
		</Inline>
	);
};
